import React from "react";
import { Calendar, CircleCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  year: "1998",
  title: "Fundação e Início Industrial",
  description: "A Doctorice inicia suas atividades com foco em manutenção e refrigeração residencial, estabelecendo desde cedo um padrão de rigor técnico e compromisso com a eficiência operacional.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_founding_workshop.png",
  align: "left"
}, {
  year: "2007",
  title: "Expansão para Saúde e Imagem",
  description: "Com a crescente demanda por precisão térmica em equipamentos de Ressonância Magnética e Tomografia, nos especializamos no setor médico-hospitalar, tornando-nos referência em chillers para diagnóstico por imagem.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_medical_equipment.png",
  align: "right"
}, {
  year: "2016",
  title: "Engenharia para Ambientes Críticos",
  description: "Consolidação da expertise em salas limpas, indústria farmacêutica e datacenters. Implementação de novas tecnologias de controle de umidade e automação para garantir estabilidade total em processos sensíveis.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_clean_room_engineering.png",
  align: "left"
}, {
  year: "2025",
  title: "Liderança e Inovação Sustentável",
  description: "Hoje, a Doctorice lidera o mercado com soluções completas de engenharia térmica, integrando fabricação própria, retrofit e serviços especializados, sempre com foco em eficiência energética e confiabilidade extrema.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_leadership_team.png",
  align: "right"
}];
export default function NossaTrajetoria() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="nossa-trajetória" ref={ref} className="relative w-full py-24 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <svg className="w-full h-full" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          <pattern id="grid-pattern" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-primary">
            Nossa Trajetória
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Décadas de evolução técnica, transformando desafios de engenharia em
            soluções térmicas de alta confiabilidade para os setores mais
            exigentes do Brasil.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-border hidden md:block" />
          
          {}
          <div className="absolute left-8 top-0 bottom-0 w-px bg-border md:hidden" />

          <div className="space-y-16 md:space-y-24">
            {milestones.map((milestone, index) => <div key={index} data-index={index} className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                {}
                <div className="absolute left-8 md:left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-background border-4 border-ring z-20 shadow-[0_0_0_4px_var(--color-background)]" />

                {}
                <div className={`w-full md:w-1/2 pl-20 md:pl-0 ${milestone.align === "right" ? "md:order-1 md:text-right md:pr-16" : "md:order-2 md:text-left md:pl-16"}`}>
                  <div className="flex flex-col gap-3">
                    <span className="font-mono text-ring text-lg font-bold tracking-wider">
                      {milestone.year}
                    </span>
                    <h3 className="text-2xl font-bold text-primary">
                      {milestone.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>

                {}
                <div className={`w-full md:w-1/2 pl-12 md:pl-0 ${milestone.align === "right" ? "md:order-2 md:pl-16" : "md:order-1 md:pr-16"}`}>
                  <Card className="overflow-hidden border-none shadow-lg bg-muted/30">
                    <CardContent className="p-0">
                      <div className="relative aspect-[4/3] w-full overflow-hidden group">
                        <img src={milestone.image} alt={milestone.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent opacity-60" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div className={`mt-24 text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex flex-col items-center gap-6 p-8 rounded-2xl bg-muted/30 border border-border/50 max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-primary">
              Faça parte da nossa história
            </h3>
            <p className="text-muted-foreground">
              Descubra como nossa experiência pode garantir a segurança e
              eficiência da sua operação hoje.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="font-semibold">
                <Link to="/ContactPage">
                  Falar com engenharia <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/CompanyPage">Conhecer a Empresa</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}